import DolfinCurrency from '../../currency'
import { Tables, TablesInsert } from '../../db_types'
import DolfinRecord, { type DataChangeReason, type PayoutSourceRecordContext } from '../../record'
import type {
  GeneratedPayoutsAnalytics,
  ManualPayoutsAnalytics,
  PayoutAdjustmentsAnalytics,
  PayoutAnalytics,
  PayoutAnalyticsParams,
  PayablePayoutsAnalytics
} from './analytics'
import {
  DolfinRpc,
  ManualPayoutExchangeRateMethod,
  ManualPayoutUpsertPayload,
  MeaningfulAuditChange,
  MeaningfulAuditChangeTableName,
  NotificationPayoutReference,
  PayoutManualAdjustmentBreakdown,
  PayoutDiscrepancyRecalculationReason,
  StatementDiscrepancyRevertResult,
  UpdateOnHoldDecision,
  UpdateOnHoldDecisionInput,
  UpdateOnHoldReviewItem,
  UpdateOnHoldReviewRow,
  UpdateOnHoldScaffoldItem,
  UpdateOnHoldScaffoldRow,
  ValueDiscrepancyAdjustmentInput
} from '../../rpc'
import { roundToDecimals } from '@dolfin/utils/numbers'
import { parseDateRange } from '@dolfin/utils/dates'
import { BadRequestError, HttpError } from '@dolfin/utils/api/endpoint/errors'
import { parseISO } from 'date-fns'
import {
  isPayoutExpired,
  isPayoutOngoing,
  isPayoutPaid,
  isPayoutStatementUnassigned,
  isPayoutValidated
} from './status'
export {
  getPayoutDisplayStatus,
  isPayoutExpired,
  isPayoutInstallmentExpired,
  isPayoutInstallmentPaid,
  isPayoutOngoing,
  isPayoutPaid,
  isPayoutPendingValidation,
  isPayoutStatementUnassigned,
  isPayoutValidated,
  type PayoutAssignmentLike,
  type PayoutDisplayStatus,
  type PayoutDisplayStatusOptions,
  type PayoutInstallmentStatus,
  type PayoutStatus,
  type PayoutStatusInstallment,
  type PayoutStatusLike
} from './status'
export {
  getPreferredDiscrepancyAmounts,
  type PreferredDiscrepancyAmount,
  type PreferredDiscrepancyAmounts
} from './discrepancyAmounts'

export type {
  GeneratedPayoutsAnalytics,
  ManualPayoutsAnalytics,
  PayoutAdjustmentsAnalytics,
  PayoutAnalytics,
  PayoutAnalyticsParams,
  PayablePayoutsAnalytics
} from './analytics'
export type {
  PayoutDiscrepancyRecalculationReason,
  StatementDiscrepancyRevertResult,
  ValueDiscrepancyAdjustmentInput
} from '../../rpc'

export type {
  UpdateOnHoldCommittedResult,
  UpdateOnHoldDecision,
  UpdateOnHoldDecisionInput,
  UpdateOnHoldPreview,
  UpdateOnHoldReviewItem
} from '../../rpc'

type NextValidatorPayout = Pick<Tables<'payouts'>, 'id'> & {
  next_validators?: string | null
}

type PayoutInstallment = Pick<Tables<'payout_installments'>, 'status' | 'statement_id'>

type PayoutValueLike = Pick<
  Tables<'payouts'>,
  'currency' | 'effective_date' | 'original_value' | 'value'
>

type PayoutSummaryLike = Pick<Tables<'payouts'>, 'status' | 'statement_id' | 'value'> & {
  payout_installments?: (PayoutInstallment | null)[] | null
}

type TopLevelPayoutLike = Pick<
  Tables<'payouts'>,
  | 'id'
  | 'profile_id'
  | 'flagged'
  | 'type'
  | 'description'
  | 'formula'
  | 'period_start'
  | 'period_end'
  | 'effective_date'
  | 'compensation'
  | 'converted_at'
  | 'currency'
  | 'exchange_rate'
  | 'original_value'
  | 'status'
  | 'statement_id'
> & {
  value: number | null
  validations: number
  validation_steps_count: number
  rule_version_id: number | null
  payout_installments?: (PayoutInstallment | null)[] | null
}

export type PayoutDiscrepancySourceType = 'deal' | 'invoice' | 'activity' | 'kpi' | 'line_item'

export type PayoutDiscrepancySection = 'validated' | 'paid' | 'statement'

type DiscrepancyDealLike = {
  id: number
  deal_id?: string | null
  name?: string | null
}

type DiscrepancyInvoiceLike = {
  id: number
  invoice_number?: string | null
  deal_id?: number | null
  deals?: DiscrepancyDealLike | null
}

type DiscrepancyActivityLike = {
  id: number
  activity_id?: string | null
  type?: string | null
  deal_id?: number | null
  deals?: DiscrepancyDealLike | null
}

type DiscrepancyLineItemLike = {
  id: number
  invoices?: DiscrepancyInvoiceLike | null
}

type DiscrepancyInstallmentLike = {
  id: number
  installment_number: number | null
  phase_id: string | null
  statement_id: number | null
}

type DiscrepancyPayoutLike = Pick<
  Tables<'payouts'>,
  | 'id'
  | 'status'
  | 'statement_id'
  | 'deal_id'
  | 'invoice_id'
  | 'activity_id'
  | 'kpi_id'
  | 'line_item_id'
> & {
  compensation_rules?: { id: number; source: PayoutDiscrepancySourceType } | null
  deals?: DiscrepancyDealLike | null
  invoices?: DiscrepancyInvoiceLike | null
  activities?: DiscrepancyActivityLike | null
  line_items?: DiscrepancyLineItemLike | null
  payout_installments?: (DiscrepancyInstallmentLike | null)[] | null
}

type UpdateOnHoldPayout = NonNullable<UpdateOnHoldReviewRow['payouts']>

type UpdateOnHoldScaffoldPayout = NonNullable<UpdateOnHoldScaffoldRow['payouts']>

type UpdateOnHoldRowRelations = {
  existing_statement?: UpdateOnHoldReviewItem['currentStatement']
  calculated_statement?: UpdateOnHoldReviewItem['calculatedStatement']
}

const isSourceDataChangeReason = (
  reason: PayoutDiscrepancyRecalculationReason['recalculation_reason']
): reason is DataChangeReason =>
  reason != null &&
  [
    'deal_data_changed',
    'invoice_data_changed',
    'activity_data_changed',
    'kpi_data_changed',
    'line_item_data_changed',
    'custom_record_data_changed'
  ].includes(reason as DataChangeReason)

const getCompensationRuleVersionChange = (
  row: UpdateOnHoldReviewRow,
  payout: UpdateOnHoldPayout | null,
  reason: PayoutDiscrepancyRecalculationReason | null
): UpdateOnHoldReviewItem['compensationRuleVersionChange'] => {
  if (reason?.recalculation_reason !== 'comp_rule_version_changed') return null

  const rule = payout?.compensation_rules
  const usedVersionId = payout?.execution?.compensation_rule_version_id
  const latestVersionId = row.calculation_execution?.compensation_rule_version_id
  if (!rule?.label || usedVersionId == null || latestVersionId == null) return null

  const versions = [...(rule.compensation_rule_versions ?? [])].sort((left, right) => {
    const leftStart = parseDateRange(left.active_period as string)?.start ?? left.created_at
    const rightStart = parseDateRange(right.active_period as string)?.start ?? right.created_at
    return leftStart.localeCompare(rightStart)
  })
  const usedVersion = versions.findIndex(version => version.id === usedVersionId) + 1
  const latestVersion = versions.findIndex(version => version.id === latestVersionId) + 1

  if (usedVersion === 0 || latestVersion === 0 || usedVersion === latestVersion) return null

  return { ruleLabel: rule.label, usedVersion, latestVersion }
}

const getUpdateOnHoldSourceProvider = (
  payout: UpdateOnHoldPayout | UpdateOnHoldScaffoldPayout | null
) =>
  payout?.deals?.crm_provider ??
  payout?.invoices?.payroll_provider ??
  payout?.invoices?.deals?.crm_provider ??
  payout?.activities?.deals?.crm_provider ??
  payout?.line_items?.invoices?.payroll_provider ??
  payout?.line_items?.invoices?.deals?.crm_provider ??
  null

export type PayoutDiscrepancyLike = Pick<
  Tables<'payout_discrepancies'>,
  | 'id'
  | 'payout_id'
  | 'discrepancy_type'
  | 'created_at'
  | 'existing_statement_id'
  | 'calculated_statement_id'
  | 'installment_phase_id'
  | 'installment_number'
> & {
  payouts?: DiscrepancyPayoutLike | null
}

export type PayoutDiscrepancyGroup<TDiscrepancy extends PayoutDiscrepancyLike> = {
  key: string
  rows: TDiscrepancy[]
  payoutStatus: string | null | undefined
  section: PayoutDiscrepancySection
  primaryRow: TDiscrepancy
  earliestCreatedAt: string | null
}

export type PayoutDiscrepancyReasonState = 'recorded' | 'unavailable'

export type ManualPayoutRateSource =
  | 'organization_custom_rates'
  | 'organization_live_rates'
  | 'custom_rate'

export type ManualPayoutExchangeRateConfigSnapshot = {
  use_fixed_exchange_rates: boolean | null
  use_invoices_exchange_rates: boolean | null
}

export type ManualPayoutExchangeRatePreview = {
  appliedRate: number | null
  exchangeRateDate: string | null
  organizationCurrency: string
  previewError?: string | null
  rateSource: ManualPayoutRateSource | null
  rateConfigSnapshot: ManualPayoutExchangeRateConfigSnapshot | null
  recipientAmount: number | null
  recipientCurrency: string
  shouldConvert: boolean
  storageExchangeRate: number | null
}

export type ManualPayoutPreviewParams = {
  amount: number
  effectiveDate: string
  exchangeRateDate?: string | null
  method: ManualPayoutExchangeRateMethod
  organizationId: number
  profileId: string
  customRate?: number | null
  readOnly?: boolean
}

export type ManualPayoutSaveParams = {
  payoutId?: number
  categoryId?: number | null
  compensationType: Tables<'payouts'>['compensation']
  description: string
  effectiveDate: string
  exchangeRateDate?: string | null
  exchangeRateMethod?: ManualPayoutExchangeRateMethod | null
  organizationId: number
  periodEnd: string
  periodStart: string
  preserveExistingExchangeRateSnapshot?: boolean
  profileId: string
  statementId: number
  type: string
  value: number
  customRate?: number | null
  readOnly?: boolean
}

export class ManualPayoutValidationError extends Error {}

export type PayoutDiscrepancyReviewModel<TDiscrepancy extends PayoutDiscrepancyLike> = {
  discrepancy: TDiscrepancy
  section: PayoutDiscrepancySection
  sourceType: PayoutDiscrepancySourceType | null
  dealInfo: DiscrepancyDealLike | null
  affectedInstallment: DiscrepancyInstallmentLike | null
  isInstallmentBased: boolean
  isMovementAlreadyApplied: boolean
  reason: {
    state: PayoutDiscrepancyReasonState
    value: PayoutDiscrepancyRecalculationReason | null
  }
}

export default class Payouts {
  private readonly payout: Tables<'payouts'>
  private rpcClient: DolfinRpc

  constructor({ rpcClient, payout }: { rpcClient: DolfinRpc; payout: Tables<'payouts'> }) {
    this.rpcClient = rpcClient
    this.payout = payout
  }

  /** Returns true when a compensation-rule payout is still active for the current period. */
  static isOngoing(payout?: Tables<'payouts'>, now = new Date()) {
    return isPayoutOngoing(payout, now)
  }

  /** Returns the number of pending payout validations for the provided months and validator. */
  static async getPendingValidationsCount(
    rpcClient: DolfinRpc,
    organizationId: number,
    monthStarts: string[],
    validatorUserId?: string
  ) {
    return rpcClient.getPendingPayoutValidationsCount(organizationId, monthStarts, validatorUserId)
  }

  static async getReportedIssuesCount(rpcClient: DolfinRpc, organizationId: number) {
    return rpcClient.getReportedIssuesCount(organizationId)
  }

  static async getNotificationReferences(
    rpcClient: DolfinRpc,
    payoutIds: number[]
  ): Promise<NotificationPayoutReference[]> {
    return rpcClient.getNotificationPayoutReferences(payoutIds)
  }

  static async getAnalytics(
    rpcClient: DolfinRpc,
    params: PayoutAnalyticsParams
  ): Promise<PayoutAnalytics> {
    return rpcClient.getPayoutAnalytics(params)
  }

  static async getGeneratedAnalytics(
    rpcClient: DolfinRpc,
    params: PayoutAnalyticsParams
  ): Promise<GeneratedPayoutsAnalytics> {
    return rpcClient.getGeneratedPayoutsAnalytics(params)
  }

  static async getPayableAnalytics(
    rpcClient: DolfinRpc,
    params: PayoutAnalyticsParams
  ): Promise<PayablePayoutsAnalytics> {
    return rpcClient.getPayablePayoutsAnalytics(params)
  }

  static async getManualPayoutAnalytics(
    rpcClient: DolfinRpc,
    params: PayoutAnalyticsParams
  ): Promise<ManualPayoutsAnalytics> {
    return rpcClient.getManualPayoutAnalytics(params)
  }

  static async getAdjustmentAnalytics(
    rpcClient: DolfinRpc,
    params: PayoutAnalyticsParams
  ): Promise<PayoutAdjustmentsAnalytics> {
    return rpcClient.getPayoutAdjustmentAnalytics(params)
  }

  static async getManualAdjustmentBreakdowns(
    rpcClient: DolfinRpc,
    organizationId: number,
    payoutIds: number[]
  ): Promise<PayoutManualAdjustmentBreakdown[]> {
    return rpcClient.getPayoutManualAdjustmentBreakdowns(organizationId, payoutIds)
  }

  static async getDiscrepanciesCount(
    rpcClient: DolfinRpc,
    organizationId: number
  ): Promise<number> {
    return rpcClient.getUnreviewedDiscrepanciesCount(organizationId)
  }

  static async getDiscrepancies(
    rpcClient: DolfinRpc,
    organizationId: number
  ): Promise<(Tables<'payout_discrepancies'> & { payouts?: Tables<'payouts'> | null })[]> {
    return rpcClient.getUnreviewedDiscrepancies(organizationId)
  }

  static async getUpdatesOnHoldReviewItems(
    rpcClient: DolfinRpc,
    organizationId: number,
    recalculationReasonRpcClient: DolfinRpc = rpcClient,
    updateIds?: number[]
  ): Promise<UpdateOnHoldReviewItem[]> {
    const rows = updateIds
      ? await rpcClient.getUpdatesOnHoldReviewRows(organizationId, updateIds)
      : await rpcClient.getUpdatesOnHoldReviewRows(organizationId)
    const [reasons, manualAdjustmentBreakdowns] = await Promise.all([
      Payouts.getDiscrepancyRecalculationReasons(
        recalculationReasonRpcClient,
        organizationId,
        rows.map(row => row.id)
      ),
      Payouts.getManualAdjustmentBreakdowns(rpcClient, organizationId, [
        ...new Set(
          rows
            .map(row => row.payout_id ?? row.payouts?.id)
            .filter((payoutId): payoutId is number => payoutId != null)
        )
      ])
    ])
    const manualAdjustmentByPayoutId = new Map(
      manualAdjustmentBreakdowns.map(adjustment => [adjustment.payout_id, adjustment])
    )

    return Promise.all(
      rows.map(async row => {
        const payout = row.payouts as UpdateOnHoldPayout | null
        const reason = reasons[row.id] ?? null
        const reasonCreatedAt = reason?.created_at
        const dataChanges =
          payout && reasonCreatedAt && isSourceDataChangeReason(reason.recalculation_reason)
            ? await (async () => {
                const target = DolfinRecord.getAuditTargetForPayoutContext(
                  payout as unknown as PayoutSourceRecordContext,
                  reason.recalculation_reason as DataChangeReason
                )
                if (!target) return []
                return Payouts.getMeaningfulAuditChanges(rpcClient, {
                  tableName: target.tableName,
                  idColumn: target.idColumn,
                  organizationId,
                  recordIds: [target.recordId],
                  beforeTimestamp: reasonCreatedAt
                })
              })()
            : []
        const calculatedNodeOutput = (row.calculated_node_output ?? {}) as Record<string, unknown>
        const payoutId = row.payout_id ?? row.payouts?.id
        const adjustment =
          payoutId != null ? (manualAdjustmentByPayoutId.get(payoutId) ?? null) : null
        const manualAdjustmentActive = payout?.edited === true && adjustment != null
        const manualAdjustmentDelta = manualAdjustmentActive ? adjustment.adjustment_value : null
        const manualAdjustmentReliable = manualAdjustmentActive && manualAdjustmentDelta != null
        const manualAdjustmentOriginalDelta =
          manualAdjustmentDelta != null && payout?.exchange_rate
            ? manualAdjustmentDelta / payout.exchange_rate
            : null
        const update: UpdateOnHoldReviewItem['update'] = {
          ...row,
          manual_adjustment_active: manualAdjustmentActive,
          manual_adjustment_delta: manualAdjustmentDelta,
          manual_adjustment_delta_reliable: manualAdjustmentReliable,
          manual_adjustment_original_delta: manualAdjustmentOriginalDelta
        }
        const calculatedStatement = (row as UpdateOnHoldRowRelations).calculated_statement ?? null
        const currentStatement =
          (row as UpdateOnHoldRowRelations).existing_statement ?? payout?.statements ?? null
        const isInReviewStatementAssignment =
          row.discrepancy_type === 'statement' &&
          currentStatement == null &&
          calculatedStatement?.status === 'in_review'
        const isCurrentPaidStatementAssignment =
          row.discrepancy_type === 'statement' &&
          currentStatement?.status === 'paid' &&
          payout?.statement_id === currentStatement.id
        const isUnassignedPaidStatementTarget =
          row.discrepancy_type === 'statement' &&
          currentStatement?.status === 'paid' &&
          payout?.statement_id == null
        const isProtectedStatementTarget =
          row.discrepancy_type === 'statement' &&
          calculatedStatement == null &&
          !isCurrentPaidStatementAssignment
        const isPaidStatementAssignment = isCurrentPaidStatementAssignment
        const isPaidCalculatedStatementTarget =
          row.discrepancy_type === 'statement' &&
          calculatedStatement?.status === 'paid' &&
          !isCurrentPaidStatementAssignment
        const usesNextAvailableStatement =
          !isPaidStatementAssignment &&
          row.discrepancy_type === 'statement' &&
          (isProtectedStatementTarget ||
            isInReviewStatementAssignment ||
            calculatedStatement == null ||
            isUnassignedPaidStatementTarget ||
            isPaidCalculatedStatementTarget)
        const nextAvailableStatement = usesNextAvailableStatement
          ? await Payouts.getNextStatementForDiscrepancy(rpcClient, row.id)
          : null
        const availableDecisions: UpdateOnHoldDecision[] = isPaidStatementAssignment
          ? ['keep_current']
          : [
              'keep_current',
              ...(!isProtectedStatementTarget && !isPaidCalculatedStatementTarget
                ? ['update_calculation' as const]
                : []),
              ...(isInReviewStatementAssignment ||
              isProtectedStatementTarget ||
              isUnassignedPaidStatementTarget ||
              isPaidCalculatedStatementTarget
                ? ['next_statement' as const]
                : []),
              ...(row.discrepancy_type === 'value' && manualAdjustmentReliable
                ? ['update_calculation_keep_adjustment' as const]
                : [])
            ]
        const profile = payout?.profiles ?? null
        const source = payout?.compensation_rules?.source ?? null
        const compensationRuleVersionChange = getCompensationRuleVersionChange(row, payout, reason)

        return {
          update,
          payout,
          profile,
          sourceLabel: source ?? (calculatedNodeOutput.source as string | null) ?? null,
          sourceProvider: getUpdateOnHoldSourceProvider(payout),
          currentStatement,
          calculatedStatement,
          nextAvailableStatement,
          reason,
          compensationRuleVersionChange,
          dataChanges,
          paidStatementLocked: isPaidStatementAssignment,
          availableDecisions
        } as UpdateOnHoldReviewItem
      })
    )
  }

  static async getUpdatesOnHoldScaffoldItems(
    rpcClient: DolfinRpc,
    organizationId: number,
    recalculationReasonRpcClient: DolfinRpc = rpcClient
  ): Promise<UpdateOnHoldScaffoldItem[]> {
    const rows = await rpcClient.getUpdatesOnHoldScaffoldRows(organizationId)
    const reasons = await Payouts.getDiscrepancyRecalculationReasons(
      recalculationReasonRpcClient,
      organizationId,
      rows.map(row => row.id)
    )

    return rows.map(row => {
      const payout = row.payouts ?? null
      return {
        update: {
          id: row.id,
          payout_id: row.payout_id,
          discrepancy_type: row.discrepancy_type,
          protected_reason: row.protected_reason,
          protected_status: row.protected_status,
          created_at: row.created_at
        },
        payout,
        profile: payout?.profiles ?? null,
        sourceLabel: payout?.compensation_rules?.source ?? null,
        sourceProvider: getUpdateOnHoldSourceProvider(payout),
        currentStatement: row.existing_statement ?? payout?.statements ?? null,
        calculatedStatement: row.calculated_statement ?? null,
        reason: reasons[row.id] ?? null
      }
    })
  }

  static validateUpdatesOnHoldDecisions(decisions: UpdateOnHoldDecisionInput[]) {
    if (decisions.length === 0 || decisions.length > 100) {
      throw new BadRequestError('Between 1 and 100 updates on hold decisions are required.')
    }
    let previousId = 0
    for (const { updateId, decision } of decisions) {
      if (!Number.isInteger(updateId) || updateId <= previousId) {
        throw new BadRequestError(
          'Updates on hold decisions must be sorted by update_id and unique.'
        )
      }
      if (
        ![
          'keep_current',
          'update_calculation',
          'next_statement',
          'update_calculation_keep_adjustment'
        ].includes(decision)
      ) {
        throw new BadRequestError('Updates on hold decision is invalid.')
      }
      previousId = updateId
    }
  }

  static async previewUpdatesOnHold(
    rpcClient: DolfinRpc,
    organizationId: number,
    decisions: UpdateOnHoldDecisionInput[]
  ) {
    Payouts.validateUpdatesOnHoldDecisions(decisions)
    return rpcClient.previewUpdatesOnHoldResolution(organizationId, decisions)
  }

  static async resolveUpdatesOnHold(
    rpcClient: DolfinRpc,
    organizationId: number,
    decisions: UpdateOnHoldDecisionInput[],
    fingerprint: string
  ) {
    Payouts.validateUpdatesOnHoldDecisions(decisions)
    if (!fingerprint) throw new BadRequestError('Updates on hold preview fingerprint is required.')
    try {
      return await rpcClient.resolveUpdatesOnHoldResolution(organizationId, decisions, fingerprint)
    } catch (error) {
      if (error && typeof error === 'object' && 'code' in error) {
        if (error.code === 'PZ001') {
          throw new HttpError(
            'Updates on hold preview is stale. Preview the selected updates again.',
            409
          )
        }
        if (error.code === 'P0001' && 'message' in error && typeof error.message === 'string') {
          throw new HttpError(error.message, 409)
        }
      }
      throw error
    }
  }

  static async getAppliedDiscrepancies(
    rpcClient: DolfinRpc,
    organizationId: number
  ): Promise<(Tables<'payout_discrepancies'> & { payouts?: Tables<'payouts'> | null })[]> {
    return rpcClient.getAppliedDiscrepancies(organizationId)
  }

  static async getDiscrepancyRecalculationReasons(
    rpcClient: DolfinRpc,
    organizationId: number,
    discrepancyIds: number[]
  ): Promise<Record<number, PayoutDiscrepancyRecalculationReason | null>> {
    if (discrepancyIds.length === 0) return {}

    const reasons = await rpcClient.getPayoutDiscrepancyRecalculationReasons(
      organizationId,
      discrepancyIds
    )
    const reasonByDiscrepancyId = new Map(
      reasons.map(reason => [reason.discrepancy_id, reason] as const)
    )

    return Object.fromEntries(
      discrepancyIds.map(discrepancyId => [
        discrepancyId,
        reasonByDiscrepancyId.get(discrepancyId) ?? null
      ])
    ) as Record<number, PayoutDiscrepancyRecalculationReason | null>
  }

  static async getMeaningfulAuditChanges(
    rpcClient: DolfinRpc,
    params: {
      tableName: MeaningfulAuditChangeTableName
      idColumn: string
      organizationId: number
      recordIds: number[]
      beforeTimestamp: string
    }
  ): Promise<MeaningfulAuditChange[]> {
    if (params.recordIds.length === 0) return []

    return rpcClient.getMeaningfulAuditChanges(params)
  }

  static async getDiscrepanciesForPayout(
    rpcClient: DolfinRpc,
    payoutId: number
  ): Promise<
    (Tables<'payout_discrepancies'> & {
      existing_statement?: {
        period_start: string
        period_end: string
        cut_period: string | null
        status: string
      } | null
      calculated_statement?: {
        period_start: string
        period_end: string
        cut_period: string | null
        status: string
      } | null
      adjustment_payouts?: Array<{
        value: number
        currency: string | null
        statement_id: number | null
        statements?: {
          period_start: string
          period_end: string
          cut_period: string | null
          status: string
        } | null
      }>
    })[]
  > {
    return rpcClient.getDiscrepanciesForPayout(payoutId)
  }

  static async getCalculationContext(rpcClient: DolfinRpc, payoutId: number) {
    return rpcClient.getPayoutCalculationContext(payoutId)
  }

  static getDiscrepancySection<TDiscrepancy extends PayoutDiscrepancyLike>(
    discrepancy: TDiscrepancy
  ): PayoutDiscrepancySection {
    if (discrepancy.discrepancy_type !== 'value') {
      return 'statement'
    }

    return isPayoutPaid(discrepancy.payouts) ? 'paid' : 'validated'
  }

  static groupDiscrepanciesBySection<TDiscrepancy extends PayoutDiscrepancyLike>(
    discrepancies: TDiscrepancy[]
  ): Record<PayoutDiscrepancySection, PayoutDiscrepancyGroup<TDiscrepancy>[]> {
    const grouped: Record<PayoutDiscrepancySection, PayoutDiscrepancyGroup<TDiscrepancy>[]> = {
      validated: [],
      paid: [],
      statement: []
    }

    discrepancies.forEach(discrepancy => {
      const section = Payouts.getDiscrepancySection(discrepancy)
      grouped[section].push({
        key: `discrepancy:${discrepancy.id}`,
        rows: [discrepancy],
        payoutStatus: discrepancy.payouts?.status,
        section,
        primaryRow: discrepancy,
        earliestCreatedAt: discrepancy.created_at ?? null
      })
    })

    return grouped
  }

  static getDiscrepancySourceType<TDiscrepancy extends PayoutDiscrepancyLike>(
    discrepancy: TDiscrepancy
  ): PayoutDiscrepancySourceType | null {
    return DolfinRecord.getDiscrepancySourceType(
      discrepancy.payouts as Parameters<typeof DolfinRecord.getDiscrepancySourceType>[0]
    ) as PayoutDiscrepancySourceType | null
  }

  static getDiscrepancyDealInfo<TDiscrepancy extends PayoutDiscrepancyLike>(
    discrepancy: TDiscrepancy
  ): DiscrepancyDealLike | null {
    return DolfinRecord.getDiscrepancyDealInfo(
      discrepancy.payouts as Parameters<typeof DolfinRecord.getDiscrepancyDealInfo>[0]
    ) as DiscrepancyDealLike | null
  }

  static isInstallmentBasedDiscrepancy<TDiscrepancy extends PayoutDiscrepancyLike>(
    discrepancy: TDiscrepancy
  ): boolean {
    return (
      discrepancy.payouts?.statement_id == null &&
      (discrepancy.payouts?.payout_installments?.length ?? 0) > 0
    )
  }

  static matchesDiscrepancyInstallment<
    TDiscrepancy extends Pick<PayoutDiscrepancyLike, 'installment_phase_id' | 'installment_number'>
  >(
    discrepancy: TDiscrepancy,
    installment: Pick<DiscrepancyInstallmentLike, 'phase_id' | 'installment_number'>
  ): boolean {
    if (discrepancy.installment_phase_id != null) {
      return installment.phase_id === discrepancy.installment_phase_id
    }
    if (discrepancy.installment_number != null) {
      return installment.installment_number === discrepancy.installment_number
    }
    return false
  }

  static getAffectedInstallment<TDiscrepancy extends PayoutDiscrepancyLike>(
    discrepancy: TDiscrepancy
  ): DiscrepancyInstallmentLike | null {
    if (!Payouts.isInstallmentBasedDiscrepancy(discrepancy)) return null

    return (
      discrepancy.payouts?.payout_installments?.find(
        (installment): installment is DiscrepancyInstallmentLike =>
          installment != null && Payouts.matchesDiscrepancyInstallment(discrepancy, installment)
      ) ??
      discrepancy.payouts?.payout_installments?.find(
        (installment): installment is DiscrepancyInstallmentLike =>
          installment != null && installment.statement_id === discrepancy.existing_statement_id
      ) ??
      null
    )
  }

  static isMovementAlreadyApplied<TDiscrepancy extends PayoutDiscrepancyLike>(
    discrepancy: TDiscrepancy
  ): boolean {
    if (
      discrepancy.discrepancy_type !== 'statement' ||
      discrepancy.calculated_statement_id == null
    ) {
      return false
    }

    if (discrepancy.payouts?.statement_id === discrepancy.calculated_statement_id) {
      return true
    }

    const matchingInstallment = discrepancy.payouts?.payout_installments?.find(
      (installment): installment is DiscrepancyInstallmentLike =>
        installment != null && Payouts.matchesDiscrepancyInstallment(discrepancy, installment)
    )

    if (matchingInstallment) {
      return matchingInstallment.statement_id === discrepancy.calculated_statement_id
    }

    return (
      discrepancy.payouts?.payout_installments?.some(
        installment =>
          installment != null &&
          Payouts.matchesDiscrepancyInstallment(discrepancy, installment) &&
          installment.statement_id === discrepancy.calculated_statement_id
      ) ?? false
    )
  }

  static buildDiscrepancyReviewModel<TDiscrepancy extends PayoutDiscrepancyLike>(
    discrepancy: TDiscrepancy,
    reason: PayoutDiscrepancyRecalculationReason | null
  ): PayoutDiscrepancyReviewModel<TDiscrepancy> {
    return {
      discrepancy,
      section: Payouts.getDiscrepancySection(discrepancy),
      sourceType: Payouts.getDiscrepancySourceType(discrepancy),
      dealInfo: Payouts.getDiscrepancyDealInfo(discrepancy),
      affectedInstallment: Payouts.getAffectedInstallment(discrepancy),
      isInstallmentBased: Payouts.isInstallmentBasedDiscrepancy(discrepancy),
      isMovementAlreadyApplied: Payouts.isMovementAlreadyApplied(discrepancy),
      reason: {
        state: reason?.recalculation_reason != null ? 'recorded' : 'unavailable',
        value: reason
      }
    }
  }

  static async getDiscrepancyIndicators(
    rpcClient: DolfinRpc,
    organizationId: number
  ): Promise<
    Pick<
      Tables<'payout_discrepancies'>,
      'payout_id' | 'reviewed' | 'resolution_type' | 'discrepancy_type'
    >[]
  > {
    return rpcClient.getDiscrepancyIndicators(organizationId)
  }

  static async applyValueDiscrepancy(
    rpcClient: DolfinRpc,
    discrepancyId: number,
    adjustmentPayoutData: ValueDiscrepancyAdjustmentInput
  ): Promise<void> {
    return rpcClient.applyValueDiscrepancy(discrepancyId, adjustmentPayoutData)
  }

  static async applyStatementDiscrepancy(
    rpcClient: DolfinRpc,
    discrepancyId: number,
    payoutId: number,
    newStatementId: number
  ): Promise<void> {
    return rpcClient.applyStatementDiscrepancy(discrepancyId, payoutId, newStatementId)
  }

  static async resolveDiscrepancy(
    rpcClient: DolfinRpc,
    discrepancyId: number,
    action: 'apply' | 'discard' | 'next_statement'
  ) {
    return rpcClient.resolvePayoutDiscrepancy(discrepancyId, action)
  }

  static async getNextStatementForDiscrepancy(rpcClient: DolfinRpc, discrepancyId: number) {
    return rpcClient.getNextStatementForPayoutDiscrepancy(discrepancyId)
  }

  static async revertStatementDiscrepancy(
    rpcClient: DolfinRpc,
    discrepancyId: number,
    payoutId: number,
    targetStatementId: number
  ): Promise<StatementDiscrepancyRevertResult> {
    return rpcClient.revertStatementDiscrepancy(discrepancyId, payoutId, targetStatementId)
  }

  static async markDiscrepancyAsReviewed(
    rpcClient: DolfinRpc,
    discrepancyId: number
  ): Promise<void> {
    return rpcClient.markDiscrepancyAsReviewed(discrepancyId)
  }

  static async resetPayoutValidation(
    rpcClient: DolfinRpc,
    payoutId: number,
    discrepancyId: number,
    newValue: number
  ): Promise<void> {
    return rpcClient.resetPayoutValidation(payoutId, discrepancyId, newValue)
  }
  static async revokePayoutValidations(rpcClient: DolfinRpc, payoutId: number): Promise<void> {
    return rpcClient.revokePayoutValidations(payoutId)
  }

  static async getStatementIdForAccrualDate(
    rpcClient: DolfinRpc,
    organizationId: number,
    profileId: string,
    accrualDate: string
  ): Promise<number | null> {
    return rpcClient.getStatementIdForAccrualDate(organizationId, profileId, accrualDate)
  }

  /** Returns whether a payout can still be adjusted based on its status and period state. */
  static canBeAdjusted(payout?: Tables<'payouts'>) {
    if (!payout) return false

    return !isPayoutValidated(payout) && !Payouts.isOngoing(payout)
  }
  static canRevokeValidations(
    payout?: Tables<'payouts'> & {
      payout_validations?: unknown[] | null
    }
  ) {
    return (
      !!payout &&
      (payout.payout_validations?.length ?? 0) > 0 &&
      !isPayoutPaid(payout) &&
      !isPayoutExpired(payout)
    )
  }

  private static toDecimalString(value: number) {
    const text = value
      .toPrecision(15)
      .replace(/(\.\d*?[1-9])0+$/, '$1')
      .replace(/\.0+$/, '')
    if (!/[eE]/.test(text)) return text

    const [mantissa, exponentRaw] = text.split(/[eE]/)
    const exponent = Number(exponentRaw)
    const negative = mantissa.startsWith('-')
    const unsigned = negative ? mantissa.slice(1) : mantissa
    const [integerPart, fractionalPart = ''] = unsigned.split('.')
    const digits = `${integerPart}${fractionalPart}`.replace(/^0+(?=\d)/, '') || '0'

    if (digits === '0') return '0'

    const decimalIndex = integerPart.length + exponent
    if (decimalIndex <= 0) {
      return `${negative ? '-' : ''}0.${'0'.repeat(Math.abs(decimalIndex))}${digits}`
    }

    if (decimalIndex >= digits.length) {
      return `${negative ? '-' : ''}${digits}${'0'.repeat(decimalIndex - digits.length)}`
    }

    return `${negative ? '-' : ''}${digits.slice(0, decimalIndex)}.${digits.slice(decimalIndex)}`
  }

  /** Sums payout values using decimal-safe normalization to avoid floating-point drift. */
  static sumValues(values: Array<number | null | undefined>) {
    let scale = 0
    const parsedValues = values
      .filter((value): value is number => value != null && Number.isFinite(value))
      .map(value => {
        const text = Payouts.toDecimalString(value)
        const negative = text.startsWith('-')
        const unsigned = negative ? text.slice(1) : text
        const [integerPart, fractionalPart = ''] = unsigned.split('.')
        scale = Math.max(scale, fractionalPart.length)
        return {
          negative,
          integerPart,
          fractionalPart
        }
      })

    let total = 0
    for (const value of parsedValues) {
      const digits = `${value.integerPart}${value.fractionalPart}`.replace(/^0+(?=\d)/, '') || '0'
      const scaled = Number(digits) * Math.pow(10, scale - value.fractionalPart.length)
      total += value.negative ? -scaled : scaled
    }

    if (scale === 0) return Number(total)

    const negative = total < 0
    const absolute = Math.abs(total)
    const text = absolute.toFixed(0).padStart(scale + 1, '0')
    const integerPart = text.slice(0, -scale) || '0'
    const fractionalPart = text.slice(-scale).replace(/0+$/, '')

    return Number(
      `${negative ? '-' : ''}${integerPart}${fractionalPart ? `.${fractionalPart}` : ''}`
    )
  }

  /** Normalizes empty installment collections to null for API payload consistency. */
  static normalizeInstallments<T>(installments: T[] | null | undefined) {
    if (!Array.isArray(installments) || installments.length === 0) {
      return null
    }

    return installments
  }

  /** Removes top-level rule-only fields before nesting payouts under deals, invoices, or KPIs. */
  static serializeNested<T extends { rule_key: string; kpi_id: number | null }>(payout: T) {
    const { rule_key: _ruleKey, kpi_id: _kpiId, ...rest } = payout
    return rest
  }

  /** Shapes a payout for the top-level period-data response payload. */
  static serializeTopLevel(payout: TopLevelPayoutLike) {
    return {
      id: payout.id,
      profile_id: payout.profile_id,
      flagged: payout.flagged,
      validations: payout.validations,
      validation_steps_count: payout.validation_steps_count,
      value: payout.value,
      type: payout.type,
      rule_version_id: payout.rule_version_id,
      description: payout.description,
      formula: payout.formula,
      period_start: payout.period_start,
      period_end: payout.period_end,
      effective_date: payout.effective_date,
      compensation: payout.compensation,
      converted_at: payout.converted_at,
      currency: payout.currency,
      exchange_rate: payout.exchange_rate,
      original_value: payout.original_value,
      status: payout.status,
      payout_installments: payout.payout_installments ?? null,
      statement_id: payout.statement_id
    }
  }

  /**
   * Computes rule-payload aggregates using explicit semantics:
   * paid and validated are payout-level persisted statuses; locked is unassigned
   * at both payout and installment level, independent of payout status.
   */
  static summarize(payouts: PayoutSummaryLike[]) {
    return {
      total_payouts_count: payouts.length,
      total_payouts_value: Payouts.sumValues(payouts.map(payout => payout.value)),
      total_payouts_paid_value: Payouts.sumValues(
        payouts.filter(payout => isPayoutPaid(payout)).map(payout => payout.value)
      ),
      total_payouts_locked_value: Payouts.sumValues(
        payouts
          .filter(payout => !isPayoutExpired(payout) && isPayoutStatementUnassigned(payout))
          .map(payout => payout.value)
      ),
      total_payouts_validated_count: payouts.filter(payout => isPayoutValidated(payout)).length
    }
  }

  /**
   * Converts a payout into the requested target currency while reusing a per-period exchange-rate cache.
   * Returns null when the payout itself has no numeric value.
   */
  static async convertValue(
    rpcClient: DolfinRpc,
    payout: PayoutValueLike,
    targetCurrency: string | undefined,
    orgCurrency: string | undefined,
    organizationId: number,
    cache: Map<string, number | null>
  ): Promise<number | null> {
    if (payout.value == null) {
      return null
    }

    if (!targetCurrency) {
      return payout.value
    }

    if (targetCurrency === payout.currency) {
      return payout.original_value
    }

    if (!orgCurrency) {
      throw new Error('Organization currency is required for payout conversion')
    }

    const cacheKey = `${targetCurrency}:${orgCurrency}:${payout.effective_date}`
    if (!cache.has(cacheKey)) {
      cache.set(
        cacheKey,
        await rpcClient.getFixedCurrencyRate(
          targetCurrency,
          orgCurrency,
          payout.effective_date,
          organizationId
        )
      )
    }

    const exchangeRate = cache.get(cacheKey)
    if (!exchangeRate) {
      throw new Error(
        `Missing exchange rate for currency ${targetCurrency} on ${payout.effective_date}`
      )
    }

    return payout.value / exchangeRate
  }

  static buildManualPayoutRateSourceMessage({
    rateSource,
    recipientCurrency
  }: {
    rateSource: ManualPayoutRateSource | null
    recipientCurrency: string
  }) {
    if (rateSource === 'organization_custom_rates') {
      return `Your organization uses a custom exchange rate for ${recipientCurrency}`
    }

    if (rateSource === 'organization_live_rates') {
      return `Your organization uses live market rates for ${recipientCurrency}`
    }

    return null
  }

  private static validateManualPayoutExchangeRateDate(value?: string | null) {
    const normalizedDate = value?.slice(0, 10) ?? null

    if (!normalizedDate) {
      throw new ManualPayoutValidationError('Exchange rate date is required')
    }

    const parsedDate = parseISO(normalizedDate)
    if (Number.isNaN(parsedDate.getTime())) {
      throw new ManualPayoutValidationError('Exchange rate date must be a valid date')
    }

    if (normalizedDate > new Date().toISOString().slice(0, 10)) {
      throw new ManualPayoutValidationError(
        "Exchange rates from the organization's settings are unavailable for future dates"
      )
    }

    return normalizedDate
  }

  static async previewManualPayoutExchangeRate(
    rpcClient: DolfinRpc,
    params: ManualPayoutPreviewParams,
    sharedCurrency?: DolfinCurrency
  ): Promise<ManualPayoutExchangeRatePreview> {
    const currencyHelper = sharedCurrency ?? new DolfinCurrency(params.organizationId, rpcClient)
    const organizationCurrency = await currencyHelper.getOrgCurrency()
    const recipientCurrency =
      (await currencyHelper.getTeamCurrency(params.profileId, params.effectiveDate, {
        throwOnError: true
      })) ?? organizationCurrency

    if (!recipientCurrency || recipientCurrency === organizationCurrency) {
      return {
        appliedRate: null,
        exchangeRateDate: null,
        organizationCurrency,
        previewError: null,
        rateSource: null,
        rateConfigSnapshot: null,
        recipientAmount: null,
        recipientCurrency: recipientCurrency ?? organizationCurrency,
        shouldConvert: false,
        storageExchangeRate: null
      }
    }

    if (params.method === 'custom') {
      if (!params.customRate || params.customRate <= 0) {
        throw new ManualPayoutValidationError('Custom exchange rate is required')
      }

      const appliedRate = roundToDecimals(1 / params.customRate, 6)
      return {
        appliedRate,
        exchangeRateDate: null,
        organizationCurrency,
        previewError: null,
        rateSource: 'custom_rate',
        rateConfigSnapshot: null,
        recipientAmount: roundToDecimals(params.amount / params.customRate, 6),
        recipientCurrency,
        shouldConvert: true,
        storageExchangeRate: params.customRate
      }
    }

    const exchangeRateDate = Payouts.validateManualPayoutExchangeRateDate(params.exchangeRateDate)
    const orgConfig = await rpcClient.getOrgCurrencyExchangeConfig(params.organizationId)
    const rateConfigSnapshot: ManualPayoutExchangeRateConfigSnapshot = {
      use_fixed_exchange_rates: orgConfig?.use_fixed_exchange_rates ?? null,
      use_invoices_exchange_rates: orgConfig?.use_invoices_exchange_rates ?? null
    }

    if (orgConfig?.use_fixed_exchange_rates) {
      const fixedRate = await rpcClient.getFixedCurrencyRate(
        recipientCurrency,
        organizationCurrency,
        exchangeRateDate,
        params.organizationId
      )
      if (fixedRate == null || !Number.isFinite(fixedRate) || fixedRate <= 0) {
        throw new ManualPayoutValidationError('Exchange rate is unavailable for the selected date')
      }

      return {
        appliedRate: roundToDecimals(1 / fixedRate, 6),
        exchangeRateDate,
        organizationCurrency,
        previewError: null,
        rateSource: 'organization_custom_rates',
        rateConfigSnapshot,
        recipientAmount: roundToDecimals(params.amount / fixedRate, 6),
        recipientCurrency,
        shouldConvert: true,
        storageExchangeRate: fixedRate
      }
    }

    const storedMarketRate = await rpcClient.getMarketCurrencyRate(
      recipientCurrency,
      organizationCurrency,
      exchangeRateDate
    )

    if (storedMarketRate != null && Number.isFinite(storedMarketRate) && storedMarketRate > 0) {
      return {
        appliedRate: roundToDecimals(1 / storedMarketRate, 6),
        exchangeRateDate,
        organizationCurrency,
        previewError: null,
        rateSource: 'organization_live_rates',
        rateConfigSnapshot,
        recipientAmount: roundToDecimals(params.amount / storedMarketRate, 6),
        recipientCurrency,
        shouldConvert: true,
        storageExchangeRate: storedMarketRate
      }
    }

    let result
    try {
      result = await currencyHelper.convert(
        1,
        recipientCurrency,
        organizationCurrency,
        exchangeRateDate,
        undefined,
        { persistRate: !params.readOnly, strictDate: params.readOnly }
      )
    } catch (error) {
      if (params.readOnly) throw error
      throw new ManualPayoutValidationError(
        error instanceof Error ? error.message : 'Failed to resolve exchange rate'
      )
    }

    if (
      !result ||
      !Number.isFinite(result.info.rate) ||
      result.info.rate <= 0 ||
      !Number.isFinite(result.result)
    ) {
      throw new ManualPayoutValidationError('Exchange rate is unavailable for the selected date')
    }

    return {
      appliedRate: roundToDecimals(1 / result.info.rate, 6),
      exchangeRateDate,
      organizationCurrency,
      previewError: null,
      rateSource: 'organization_live_rates',
      rateConfigSnapshot,
      recipientAmount: roundToDecimals(params.amount / result.info.rate, 6),
      recipientCurrency,
      shouldConvert: true,
      storageExchangeRate: roundToDecimals(result.info.rate, 6)
    }
  }

  static async saveManualPayout(
    rpcClient: DolfinRpc,
    params: ManualPayoutSaveParams
  ): Promise<Tables<'payouts'>> {
    const payload = await Payouts.prepareManualPayout(rpcClient, params)
    return rpcClient.upsertManualPayout({ ...payload, statement_id: params.statementId })
  }

  static async prepareManualPayout(
    rpcClient: DolfinRpc,
    params: Omit<ManualPayoutSaveParams, 'statementId'>,
    sharedCurrency?: DolfinCurrency
  ): Promise<Omit<ManualPayoutUpsertPayload, 'statement_id'>> {
    const existingPayout =
      params.payoutId != null
        ? await rpcClient.getPayoutById(params.payoutId, params.organizationId)
        : null

    if (
      existingPayout &&
      (existingPayout.period_start !== params.periodStart ||
        existingPayout.period_end !== params.periodEnd)
    ) {
      throw new ManualPayoutValidationError('Manual payout period cannot be changed')
    }

    if (
      existingPayout &&
      params.preserveExistingExchangeRateSnapshot &&
      ((params.exchangeRateMethod != null &&
        params.exchangeRateMethod !== existingPayout.manual_exchange_rate_method) ||
        (params.exchangeRateMethod === 'organization_settings' &&
          params.exchangeRateDate?.slice(0, 10) !== existingPayout.converted_at) ||
        (params.exchangeRateMethod === 'custom' &&
          params.customRate != null &&
          params.customRate !== existingPayout.exchange_rate))
    ) {
      throw new ManualPayoutValidationError(
        'Manual payout exchange-rate snapshot cannot be changed'
      )
    }

    const isDeduction = params.type === 'clawback'
    const signedValue = isDeduction ? -Math.abs(params.value) : params.value

    let exchangeRatePayload: Pick<
      TablesInsert<'payouts'>,
      | 'converted_at'
      | 'currency'
      | 'exchange_rate'
      | 'manual_exchange_rate_from_currency'
      | 'manual_exchange_rate_method'
      | 'manual_exchange_rate_source'
      | 'original_value'
    > = {
      converted_at: null,
      currency: null,
      exchange_rate: null,
      manual_exchange_rate_from_currency: null,
      manual_exchange_rate_method: null,
      manual_exchange_rate_source: null,
      original_value: null
    }

    if (existingPayout && params.preserveExistingExchangeRateSnapshot) {
      exchangeRatePayload = {
        converted_at: existingPayout.converted_at,
        currency: existingPayout.currency,
        exchange_rate: existingPayout.exchange_rate,
        manual_exchange_rate_from_currency: existingPayout.manual_exchange_rate_from_currency,
        manual_exchange_rate_method: existingPayout.manual_exchange_rate_method,
        manual_exchange_rate_source: existingPayout.manual_exchange_rate_source,
        original_value: existingPayout.original_value
      }
    } else if (params.exchangeRateMethod) {
      const preview = await Payouts.previewManualPayoutExchangeRate(
        rpcClient,
        {
          amount: params.value,
          customRate: params.customRate,
          effectiveDate: params.effectiveDate,
          exchangeRateDate: params.exchangeRateDate,
          method: params.exchangeRateMethod,
          organizationId: params.organizationId,
          profileId: params.profileId,
          readOnly: params.readOnly
        },
        sharedCurrency
      )

      if (preview.shouldConvert) {
        exchangeRatePayload = {
          converted_at:
            params.exchangeRateMethod === 'organization_settings' ? preview.exchangeRateDate : null,
          currency: preview.recipientCurrency,
          exchange_rate: preview.storageExchangeRate,
          manual_exchange_rate_from_currency: preview.organizationCurrency,
          manual_exchange_rate_method: params.exchangeRateMethod,
          manual_exchange_rate_source: preview.rateSource,
          original_value: isDeduction
            ? -Math.abs(preview.recipientAmount ?? 0)
            : (preview.recipientAmount ?? 0)
        }
      }
    }

    if (
      existingPayout &&
      (exchangeRatePayload.manual_exchange_rate_from_currency !==
        existingPayout.manual_exchange_rate_from_currency ||
        exchangeRatePayload.manual_exchange_rate_method !==
          existingPayout.manual_exchange_rate_method ||
        exchangeRatePayload.manual_exchange_rate_source !==
          existingPayout.manual_exchange_rate_source)
    ) {
      throw new ManualPayoutValidationError(
        'Manual payout exchange-rate snapshot cannot be changed'
      )
    }

    return {
      id: params.payoutId,
      category_id: params.categoryId ?? null,
      compensation: params.compensationType,
      description: params.description,
      effective_date: params.effectiveDate,
      organization_id: params.organizationId,
      period_end: params.periodEnd,
      period_start: params.periodStart,
      profile_id: params.profileId,
      type: params.type,
      value: signedValue,
      ...exchangeRatePayload
    }
  }

  /** Loads validator rows for the current payout instance. */
  async getNextValidators() {
    return await this.rpcClient.getNextValidators(this.payout.id)
  }

  /** Resolves the validation actions available to a specific profile for this payout. */
  async availableActionsForProfile(profileId: string) {
    const nextValidators = await this.getNextValidators()
    if (nextValidators) {
      const profileValidator = nextValidators.find(vals => vals.profile_id === profileId)

      if (profileValidator) {
        return {
          canValidate: !!profileValidator && !Payouts.isOngoing(this.payout),
          validatorCanAdjust: profileValidator?.can_adjust && !Payouts.isOngoing(this.payout)
        }
      }
    }

    return {
      canValidate: false,
      validatorCanAdjust: false
    }
  }

  /** Returns the next validator ids as a comma-separated string, resolving them on demand if needed. */
  static async getNextValidatorsString(rpcClient: DolfinRpc, payout: NextValidatorPayout) {
    if (payout.next_validators) {
      return payout.next_validators
    }

    const validators = await rpcClient.getNextValidators(payout.id)
    if (!validators || validators.length === 0) {
      return null
    }

    return validators.map(validator => validator.profile_id).join(',')
  }

  // Calling get_next_validators for every payout at once overwhelms the local stack
  // on large periods, so resolve them in small batches.
  /** Resolves next validators in small batches to avoid overwhelming local execution. */
  static async loadNextValidatorsByPayoutId(
    rpcClient: DolfinRpc,
    payouts: NextValidatorPayout[],
    chunkSize = 25
  ) {
    const uniquePayouts = Array.from(new Map(payouts.map(payout => [payout.id, payout])).values())
    const nextValidatorsByPayoutId = new Map<number, string | null>()

    for (let index = 0; index < uniquePayouts.length; index += chunkSize) {
      const chunk = uniquePayouts.slice(index, index + chunkSize)
      const resolvedChunk = await Promise.all(
        chunk.map(
          async payout =>
            [payout.id, await Payouts.getNextValidatorsString(rpcClient, payout)] as const
        )
      )

      for (const [payoutId, nextValidators] of resolvedChunk) {
        nextValidatorsByPayoutId.set(payoutId, nextValidators)
      }
    }

    return nextValidatorsByPayoutId
  }
}
